/**
 * @file routes/_app/settings/numbering.tsx
 * @description 編號序列管理頁面（Task 68）。
 *
 * 唯讀列表，管理員可點擊「重置序號」將 currentValue 歸零。
 * 重置後下一次發號從 1 開始，需謹慎操作（可能造成重複編號）。
 *
 * 序46（2026-09-25）：新增「使用說明」與「編號分類 / 格式示例」欄（中文化 scope 代碼），
 * 並說明編號分類由系統業務規則固定、不可自訂（每種分類都綁定一段發號程式與格式）。
 *
 * 視覺：ListPageShell + SectionCard + StatusPill
 */

import { createFileRoute } from '@tanstack/react-router';
import { Alert, Button, Form, InputNumber, Modal, Select, Space, Table, Typography } from 'antd';
import { useState } from 'react';
import { message, modal } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import { RotateCcw, Hash, Plus, PencilLine } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { describeNumberingSequence } from '@/lib/numbering-labels';
import { PageShell, SectionCard } from '@/components/shell';
import { color, font, text, weight } from '@/styles/tokens';

/**
 * 序120：可新增的編號規則分類預設（驗證註冊碼 / 計劃編號 / 證書字號）。
 * value 對應後端 nextNumber 的 scope / scopeKey 組合。
 */
const RULE_PRESETS: { label: string; scope: string; scopeKey: string; year: number }[] = [
  { label: '驗證註冊碼（有機 TOP，ZH-CAS 序號）', scope: 'client', scopeKey: 'TOP', year: 0 },
  { label: '驗證註冊碼（產銷履歷 TAP）', scope: 'client', scopeKey: 'TAP', year: 0 },
  { label: '驗證註冊碼（友善 ECO）', scope: 'client', scopeKey: 'ECO', year: 0 },
  {
    label: `計劃編號（稽核計劃，${new Date().getFullYear()} 年度）`,
    scope: 'audit-plan',
    scopeKey: '',
    year: new Date().getFullYear(),
  },
  {
    label: `證書字號（有機 農糧 1-011-0…，${new Date().getFullYear()} 年度）`,
    scope: 'cert-doc',
    scopeKey: 'top-agri',
    year: new Date().getFullYear(),
  },
  {
    label: `證書字號（有機 加分流 1-011-1…，${new Date().getFullYear()} 年度）`,
    scope: 'cert-doc',
    scopeKey: 'top-processing',
    year: new Date().getFullYear(),
  },
  {
    label: `證書字號（履歷 ZH-yyTAP…，${new Date().getFullYear()} 年度）`,
    scope: 'cert-doc',
    scopeKey: 'tap',
    year: new Date().getFullYear(),
  },
];

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/numbering')({ component: NumberingPage });

/**
 * 編號序列實體類型（對應後端 numberingSequences 表）。
 */
interface NumberingSequence {
  id: number;
  scope: string;
  scopeKey: string;
  year: number;
  currentValue: number;
  updatedAt: string;
}

/**
 * 後端響應結構（非分頁列表，返回全量 items）。
 */
interface NumberingListResponse {
  items: NumberingSequence[];
}

/** 編號序列管理頁面（唯讀 + 重置操作） */
function NumberingPage() {
  const qc = useQueryClient();

  /** 查詢所有編號序列 */
  const { data, isLoading } = useQuery({
    queryKey: ['settings-numbering'],
    queryFn: () => apiClient.get<NumberingListResponse>('/api/settings/numbering'),
  });

  /**
   * 重置指定序列 Mutation。
   * 使用 POST /:id/reset，需 numbering-rule.write 能力（admin only）。
   */
  const reset = useMutation({
    mutationFn: (id: number) =>
      apiClient.post<{ ok: boolean }>(`/api/settings/numbering/${id}/reset`),
    onSuccess: () => {
      message.success('序號已重置為 0');
      void qc.invalidateQueries({ queryKey: ['settings-numbering'] });
    },
  });

  // 序120：新增編號規則 Modal 狀態
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm<{ preset: number; currentValue: number }>();

  /** 新增/更新編號規則（序120；同 scope+key+year 就地更新起始值） */
  const upsert = useMutation({
    mutationFn: (v: { scope: string; scopeKey: string; year: number; currentValue: number }) =>
      apiClient.post('/api/settings/numbering', v),
    onSuccess: () => {
      message.success('編號規則已保存');
      setAddOpen(false);
      addForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['settings-numbering'] });
    },
    onError: (e: Error) => {
      console.error('[NumberingPage] upsert failed', e);
      message.error(e.message || '保存失敗');
    },
  });

  /** 直接設定序號值（序97：承接存量資料起點，如驗證註冊碼設 1365） */
  const setValue = useMutation({
    mutationFn: (v: { id: number; value: number }) =>
      apiClient.post(`/api/settings/numbering/${v.id}/set`, { value: v.value }),
    onSuccess: () => {
      message.success('序號已更新');
      void qc.invalidateQueries({ queryKey: ['settings-numbering'] });
    },
    onError: (e: Error) => {
      console.error('[NumberingPage] set value failed', e);
      message.error(e.message || '更新失敗');
    },
  });

  /** 「設定序號」彈窗：輸入新的 currentValue */
  const openSetValue = (row: NumberingSequence) => {
    let nextValue = row.currentValue;
    modal.confirm({
      title: `設定序號：${row.scope}/${row.scopeKey || '—'}/${row.year}`,
      content: (
        <div style={{ marginTop: 8 }}>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            下一個發出的編號＝設定值＋1。承接存量資料（如原客戶已到 1365 號）時填 1365。
          </Typography.Paragraph>
          <InputNumber
            min={0}
            defaultValue={row.currentValue}
            style={{ width: '100%' }}
            onChange={(v) => {
              nextValue = v ?? row.currentValue;
            }}
          />
        </div>
      ),
      okText: '保存',
      cancelText: '取消',
      onOk: () => setValue.mutateAsync({ id: row.id, value: nextValue }),
    });
  };

  const columns: ColumnsType<NumberingSequence> = [
    {
      // 序46：把 scope/scopeKey/year 技術代碼翻成中文分類，並給出格式示例
      title: '編號分類',
      key: '_category',
      width: 280,
      render: (_, row) => {
        const d = describeNumberingSequence(row.scope, row.scopeKey, row.year);
        return (
          <div>
            <div style={{ color: color.text.primary, fontWeight: weight.medium }}>{d.category}</div>
            <div style={{ fontFamily: font.mono, fontSize: text.xs, color: color.text.tertiary }}>
              例：{d.example}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      width: 130,
      render: (v: string) => (
        <span
          style={{
            fontFamily: font.mono,
            fontSize: text.sm,
            color: color.brand.primary,
            fontWeight: weight.medium,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: 'Scope Key',
      dataIndex: 'scopeKey',
      width: 160,
      render: (v: string) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
          {v || '—'}
        </span>
      ),
    },
    {
      title: '年度',
      dataIndex: 'year',
      width: 80,
      render: (v: number) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
          {v}
        </span>
      ),
    },
    {
      title: '當前序號',
      dataIndex: 'currentValue',
      width: 110,
      render: (v: number) => (
        <span
          style={{
            fontFamily: font.mono,
            fontVariantNumeric: 'tabular-nums',
            fontSize: text.md,
            fontWeight: weight.bold,
            color: v > 0 ? color.text.primary : color.text.muted,
          }}
        >
          {v.toLocaleString()}
        </span>
      ),
    },
    {
      title: '最後更新',
      dataIndex: 'updatedAt',
      render: (v: string) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.tertiary }}>
          {new Date(v).toLocaleString('zh-TW')}
        </span>
      ),
    },
    {
      title: '操作',
      key: '_actions',
      width: 210,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          {/* 序120/97：直接設定當前序號（承接存量編號起點） */}
          <Button size="small" icon={<PencilLine size={12} />} onClick={() => openSetValue(row)}>
            設定序號
          </Button>
          <Button
            size="small"
            danger
            icon={<RotateCcw size={12} />}
            onClick={() =>
              modal.confirm({
                title: '確定重置序號？',
                content: (
                  <span>
                    重置後下一個
                    <Typography.Text code>
                      {row.scope}/{row.scopeKey || '—'}/{row.year}
                    </Typography.Text>
                    編號將從 1 開始，可能造成重複，請謹慎操作。
                  </span>
                ),
                okText: '確認重置',
                okButtonProps: { danger: true },
                onOk: () => reset.mutate(row.id),
              })
            }
          >
            重置序號
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageShell
      title="編號序列管理"
      description="查看並管理系統各業務實體的自動編號序列。重置序號操作不可逆，請謹慎操作。"
    >
      {/* 序46：使用說明（回應「此功能怎麼用、能否自訂編號分類」） */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="使用說明"
        description={
          <ol style={{ margin: 0, paddingInlineStart: 18, lineHeight: 1.8 }}>
            <li>
              系統在建立客戶、案件、稽核計劃、發證等業務時<strong>自動取號</strong>
              ：下一個編號 ＝「當前序號」＋ 1。本頁用來查看與調整各分類目前發到第幾號。
            </li>
            <li>
              <strong>設定序號</strong>
              ：承接既有紙本／舊系統編號時使用。例如驗證註冊碼（有機）舊資料已編到 ZH-CAS1365，
              將當前序號設為 1365，下一位新客戶即為 ZH-CAS1366。
            </li>
            <li>
              <strong>新增編號規則</strong>
              ：某分類尚未出現在列表（例如新年度尚未發過證書字號）但需預先設定起始號時使用；
              分類已存在則直接更新其當前序號。
            </li>
            <li>
              <strong>編號分類由系統業務規則固定</strong>
              ，無法自行新增分類：每種分類都對應一段固定的發號程式與編號格式（如 ZH-CAS0001、
              2026-CAS0001-01、1-011-026001），自訂的新分類不會被任何業務使用。如需新的編號格式，請提出需求由系統調整。
            </li>
            <li>
              案件、實驗室報告、不符合事項等序列依客戶或案件自動分區產生（如「案件編號（產銷履歷
              客戶 TAP1065，2026 年度）」），一般無需手動調整。
            </li>
          </ol>
        }
      />
      <Alert
        type="warning"
        showIcon
        message="重置序號可能導致已發出的業務編號重複，請在確認無在途業務後方可操作。"
        style={{ marginBottom: 16 }}
      />
      <SectionCard
        icon={<Hash size={14} />}
        title="編號序列列表"
        description={`共 ${data?.items.length ?? 0} 個序列`}
        actions={
          <Button
            type="primary"
            size="small"
            icon={<Plus size={13} />}
            onClick={() => setAddOpen(true)}
          >
            新增編號規則
          </Button>
        }
      >
        <Table<NumberingSequence>
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isLoading}
          pagination={false}
          scroll={{ x: 1180 }}
          size="middle"
        />
      </SectionCard>

      {/* 序120：新增編號規則（分類：驗證註冊碼 / 計劃編號 / 證書字號） */}
      <Modal
        title="新增編號規則"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={() => addForm.submit()}
        confirmLoading={upsert.isPending}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={addForm}
          layout="vertical"
          onFinish={(v) => {
            const preset = RULE_PRESETS[v.preset];
            if (!preset) return;
            upsert.mutate({
              scope: preset.scope,
              scopeKey: preset.scopeKey,
              year: preset.year,
              currentValue: v.currentValue,
            });
          }}
        >
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            編號分類由系統業務規則固定（下拉可選的即全部可預設的分類）；選擇分類並填入當前序號，
            下一次發號即為「當前序號＋1」。
          </Typography.Paragraph>
          <Form.Item
            name="preset"
            label="編號分類"
            rules={[{ required: true, message: '請選擇編號分類' }]}
          >
            <Select
              placeholder="驗證註冊碼 / 計劃編號 / 證書字號…"
              options={RULE_PRESETS.map((p, i) => ({ value: i, label: p.label }))}
            />
          </Form.Item>
          <Form.Item
            name="currentValue"
            label="當前序號（下一次發號＝此值＋1）"
            tooltip="承接存量資料時填既有最大號，如原客戶註冊碼已到 1365 → 填 1365"
            rules={[{ required: true, message: '請輸入當前序號' }]}
            initialValue={0}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
}
